import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import { GlobalTable, HeaderLine, Td, Tr } from '../../../../../components/styles/table-style';
import { Pencil, Trash2, Settings, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import ApiService from '../../../../../services/ApiService';
import {
  Th,
  ThContent,
  ConfigMenuWrapper,
  ConfigMenuButton,
  ColumnMenu,
  ColumnMenuList,
  ColumnMenuItem,
  HiddenCheckbox,
  StyledCheckbox,
} from './style';

const ActionButton = styled.button<{ variant: 'edit' | 'delete' }>`
  background: ${props => props.variant === 'edit' ? '#E6F3FA' : '#FEECEC'};
  border: none;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.1s, filter 0.2s;
  &:hover {
    filter: brightness(0.95);
    transform: scale(1.05);
  }
`;

const TableScrollContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
  border-radius: 8px;
  background: #fff;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; }
`;

const ScenarioTable: React.FC = () => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef<HTMLTableCellElement>(null);

  const handleDelete = async (id: number, nome: string) => {
    if (!window.confirm(`Excluir o cenário "${nome}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await ApiService.fiscal.deleteTaxConfig(id);
      setScenarios(prev => prev.filter(s => s.id !== id));
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Erro ao excluir cenário.');
    }
  };

  const fetchScenarios = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await ApiService.fiscal.getTaxConfigs();
      const data = Array.isArray(response.data) ? response.data : response.data.data;
      setScenarios(data || []);
    } catch (error) {
      console.error('Erro ao buscar cenários:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchScenarios(); }, [fetchScenarios]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target as Node)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const columns = useMemo<ColumnDef<any>[]>(() => [
    {
      accessorKey: 'nome',
      header: 'Nome',
      cell: ({ getValue }) => <strong>{(getValue() as string) || 'Sem nome'}</strong>,
    },
    {
      accessorKey: 'cnpjOrigem',
      header: 'CNPJ de origem',
      enableSorting: false,
      accessorFn: (row) => `${row.CompanyEmissor?.cnpj || '—'} · ${row.CompanyEmissor?.state?.name || ''}`,
      cell: ({ getValue }) => <span style={{ color: '#999', fontSize: '13px' }}>{getValue() as string}</span>,
    },
    {
      accessorKey: 'cfop',
      header: 'CFOP',
      accessorFn: (row) => row.Cfop?.code,
      cell: ({ getValue }) => <strong style={{ color: '#0085CC' }}>{getValue() as string}</strong>,
    },
    {
      accessorKey: 'aliquotaIPI',
      header: 'IPI%',
      cell: ({ getValue }) => `${getValue() || '0.00'}%`,
    },
    {
      accessorKey: 'cenarios',
      header: 'Cenários',
      enableSorting: false,
      accessorFn: (row) => row.TaxScenario?.length || 0,
      cell: ({ getValue }) => <strong>{getValue() as number}</strong>,
    },
    {
      id: 'acoes',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <ActionButton
            variant="edit"
            title="Editar"
            onClick={() => navigate(`/panel/invoice/tax-scenario/create?editId=${row.original.id}`)}
          >
            <Pencil size={16} color="#0085CC" />
          </ActionButton>
          <ActionButton
            variant="delete"
            title="Excluir"
            onClick={() => handleDelete(row.original.id, row.original.nome || 'sem nome')}
          >
            <Trash2 size={16} color="#F75A68" />
          </ActionButton>
        </div>
      ),
    },
  ], [navigate]);

  const table = useReactTable({
    data: scenarios,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <TableScrollContainer>
      <GlobalTable>
        <thead>
          <HeaderLine>
            {table.getHeaderGroups().map(hg =>
              hg.headers.map(header => (
                <Th
                  key={header.id}
                  $sortable={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <ThContent>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {(() => {
                      const dir = header.column.getIsSorted() as string;
                      if (dir === 'asc') return <ChevronUp size={12} />;
                      if (dir === 'desc') return <ChevronDown size={12} />;
                      if (header.column.getCanSort()) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
                      return null;
                    })()}
                  </ThContent>
                </Th>
              ))
            )}
            <th
              ref={columnMenuRef}
              style={{ width: '32px', background: '#f5f5f5', border: 0, padding: '0 4px', verticalAlign: 'middle' }}
            >
              <ConfigMenuWrapper>
                <ConfigMenuButton onClick={() => setShowColumnMenu(v => !v)} title="Mostrar/ocultar colunas">
                  <Settings size={16} />
                </ConfigMenuButton>
                {showColumnMenu && (
                  <ColumnMenu>
                    <ColumnMenuList>
                      {table.getAllLeafColumns().map(col => {
                        if (col.id === 'acoes') return null;
                        const visible = col.getIsVisible();
                        return (
                          <ColumnMenuItem key={col.id}>
                            <HiddenCheckbox checked={visible} onChange={col.getToggleVisibilityHandler()} />
                            <StyledCheckbox $checked={visible} />
                            {col.columnDef.header as string}
                          </ColumnMenuItem>
                        );
                      })}
                    </ColumnMenuList>
                  </ColumnMenu>
                )}
              </ConfigMenuWrapper>
            </th>
          </HeaderLine>
        </thead>
        <tbody>
          {isLoading ? (
            <Tr><Td colSpan={columns.length + 1} style={{ textAlign: 'center' }}>Carregando cenários...</Td></Tr>
          ) : table.getRowModel().rows.length === 0 ? (
            <Tr><Td colSpan={columns.length + 1} style={{ textAlign: 'center' }}>Nenhum cenário encontrado.</Td></Tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <Tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>
                ))}
                <Td />
              </Tr>
            ))
          )}
        </tbody>
      </GlobalTable>
    </TableScrollContainer>
  );
};

export default ScenarioTable;
